import React from "react";
import { useTranslation } from "react-i18next";

import { ACTION_ICONS } from "../View";
import { ActionItem } from "./types";
import {
    Card,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    SmallToggle,
    LanguageTabs,
    LanguageTab,
    InputLabel,
    Input,
    InputWrapper,
    ResetButton,
    VisibilityButton,
    ActionConfigCard,
    ActionConfigHeader,
    ActionConfigTitle,
    ActionConfigControls,
    ActionConfigBody,
    ActionFieldRow,
    ActionTextRow,
    ResetAllButton,
    IconCircle,
    IconPickerWrapper,
    IconPickerButton,
    IconPickerDropdown,
    IconPickerGrid,
    IconPickerItem,
    ColorPickerRow,
    ColorInput,
    ColorLabel,
} from "./styled";

type ActionsTableProps = {
    mainColor: string;
    languages: Array<{ code: string; label: string }>;
    selectedButtonsLang: string;
    onButtonsLangChange: (lang: string) => void;
    actions: ActionItem[];
    defaultActions: ActionItem[];
    iconPickerOpen: number | null;
    iconPickerRef: React.RefObject<HTMLDivElement>;
    onSetIconPickerOpen: (id: number | null) => void;
    onToggleActionActive: (id: number) => void;
    onToggleActionTitleVisible: (id: number) => void;
    onToggleActionDescVisible: (id: number) => void;
    onUpdateActionField: (
        id: number,
        field: "title" | "description",
        value: string
    ) => void;
    onUpdateActionIcon: (id: number, iconKey: string) => void;
    onUpdateActionColor: (id: number, color: string) => void;
    onResetActionTitle: (id: number) => void;
    onResetActionDescription: (id: number) => void;
    onResetActionTexts: (id: number) => void;
    onResetAllActions: () => void;
};

export function ActionsTable({
    mainColor,
    languages,
    selectedButtonsLang,
    onButtonsLangChange,
    actions,
    defaultActions,
    iconPickerOpen,
    iconPickerRef,
    onSetIconPickerOpen,
    onToggleActionActive,
    onToggleActionTitleVisible,
    onToggleActionDescVisible,
    onUpdateActionField,
    onUpdateActionIcon,
    onUpdateActionColor,
    onResetActionTitle,
    onResetActionDescription,
    onResetActionTexts,
    onResetAllActions,
}: ActionsTableProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card>
            <SectionHeader>
                <SectionTitle>{t("Action buttons")}</SectionTitle>
                <ResetButton
                    data-testid="scan-to-actions-reset-all-actions"
                    mainColor={mainColor}
                    onClick={onResetAllActions}
                    title={t("Reset all")}
                    style={{
                        padding: "6px 12px",
                        width: "auto",
                        height: "auto",
                    }}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{ width: 14, height: 14 }}
                    >
                        <path d="M1 4v6h6M23 20v-6h-6" />
                        <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                    </svg>
                    <span style={{ marginLeft: 4, fontSize: 12 }}>
                        {t("Reset all")}
                    </span>
                </ResetButton>
            </SectionHeader>

            <LanguageTabs data-testid="scan-to-actions-buttons-lang-tabs">
                {languages.map((lang) => (
                    <LanguageTab
                        key={lang.code}
                        data-testid={`scan-to-actions-buttons-lang-${lang.code}`}
                        isActive={selectedButtonsLang === lang.code}
                        mainColor={mainColor}
                        onClick={() => onButtonsLangChange(lang.code)}
                    >
                        {lang.label}
                    </LanguageTab>
                ))}
            </LanguageTabs>

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                }}
            >
                {[...actions]
                    .sort((a, b) => a.order - b.order)
                    .map((action) => (
                        <ActionConfigCard
                            key={action.id}
                            data-testid={`scan-to-actions-card-${action.id}`}
                            isActive={action.active}
                        >
                            <ActionConfigHeader>
                                <ActionConfigTitle>
                                    <IconCircle
                                        bgColor={action.iconColor}
                                        size={30}
                                    >
                                        <svg viewBox="0 0 24 24">
                                            <path
                                                d={
                                                    ACTION_ICONS[action.icon] ||
                                                    ACTION_ICONS.restaurant
                                                }
                                            />
                                        </svg>
                                    </IconCircle>
                                    {action.title[selectedButtonsLang] ||
                                        action.title.fr}
                                </ActionConfigTitle>
                                <ActionConfigControls>
                                    <ToggleContainer>
                                        <ToggleLabel style={{ fontSize: 12 }}>
                                            {t("Active")}
                                        </ToggleLabel>
                                        <SmallToggle
                                            data-testid={`scan-to-actions-active-toggle-${action.id}`}
                                            isActive={action.active}
                                            mainColor={mainColor}
                                            onClick={() =>
                                                onToggleActionActive(action.id)
                                            }
                                            aria-label={t(
                                                "Toggle action visibility"
                                            )}
                                        />
                                    </ToggleContainer>
                                </ActionConfigControls>
                            </ActionConfigHeader>

                            <ActionConfigBody>
                                {/* Icon picker */}
                                <ActionFieldRow>
                                    <InputLabel>{t("Icon")}</InputLabel>
                                    <IconPickerWrapper
                                        ref={
                                            iconPickerOpen === action.id
                                                ? iconPickerRef
                                                : undefined
                                        }
                                    >
                                        <IconPickerButton
                                            data-testid={`scan-to-actions-icon-picker-btn-${action.id}`}
                                            mainColor={mainColor}
                                            onClick={() =>
                                                onSetIconPickerOpen(
                                                    iconPickerOpen === action.id
                                                        ? null
                                                        : action.id
                                                )
                                            }
                                        >
                                            <IconCircle
                                                bgColor={action.iconColor}
                                                size={24}
                                            >
                                                <svg viewBox="0 0 24 24">
                                                    <path
                                                        d={
                                                            ACTION_ICONS[
                                                                action.icon
                                                            ] ||
                                                            ACTION_ICONS.restaurant
                                                        }
                                                    />
                                                </svg>
                                            </IconCircle>
                                            {t("Change icon")}
                                            <svg
                                                width="10"
                                                height="10"
                                                viewBox="0 0 24 24"
                                                fill="#666"
                                            >
                                                <path d="M7 10l5 5 5-5z" />
                                            </svg>
                                        </IconPickerButton>

                                        {iconPickerOpen === action.id && (
                                            <IconPickerDropdown>
                                                <IconPickerGrid>
                                                    {Object.entries(
                                                        ACTION_ICONS
                                                    ).map(([key, path]) => (
                                                        <IconPickerItem
                                                            key={key}
                                                            data-testid={`scan-to-actions-icon-option-${action.id}-${key}`}
                                                            isActive={
                                                                action.icon ===
                                                                key
                                                            }
                                                            mainColor={
                                                                mainColor
                                                            }
                                                            onClick={() =>
                                                                onUpdateActionIcon(
                                                                    action.id,
                                                                    key
                                                                )
                                                            }
                                                            title={key}
                                                        >
                                                            <svg viewBox="0 0 24 24">
                                                                <path
                                                                    d={path}
                                                                />
                                                            </svg>
                                                        </IconPickerItem>
                                                    ))}
                                                </IconPickerGrid>
                                                <ColorPickerRow>
                                                    <ColorLabel>
                                                        {t("Icon color")}
                                                    </ColorLabel>
                                                    <ColorInput
                                                        data-testid={`scan-to-actions-color-picker-${action.id}`}
                                                        type="color"
                                                        value={action.iconColor}
                                                        onChange={(e) =>
                                                            onUpdateActionColor(
                                                                action.id,
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <span
                                                        style={{
                                                            fontSize: 11,
                                                            color: "#666",
                                                        }}
                                                    >
                                                        {action.iconColor}
                                                    </span>
                                                </ColorPickerRow>
                                            </IconPickerDropdown>
                                        )}
                                    </IconPickerWrapper>
                                </ActionFieldRow>

                                {/* Title & Description header */}
                                <div
                                    style={{
                                        borderTop: "1px solid #e0e0e0",
                                        paddingTop: "10px",
                                        marginTop: "4px",
                                    }}
                                >
                                    <ActionTextRow
                                        style={{
                                            borderBottom: "1px solid #e0e0e0",
                                            paddingBottom: "10px",
                                            marginBottom: "0",
                                        }}
                                    >
                                        <InputLabel></InputLabel>
                                        <InputLabel style={{ fontWeight: 500 }}>
                                            {t("Default text")}
                                        </InputLabel>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                width: "100%",
                                            }}
                                        >
                                            <InputLabel
                                                style={{
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {t("Customized")}
                                            </InputLabel>
                                            <ResetAllButton
                                                data-testid={`scan-to-actions-reset-texts-${action.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetActionTexts(
                                                        action.id
                                                    )
                                                }
                                            >
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                >
                                                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                                                </svg>
                                                {t("Reset")}
                                            </ResetAllButton>
                                        </div>
                                    </ActionTextRow>

                                    {/* Title row */}
                                    <ActionTextRow>
                                        <InputLabel>{t("Title")}</InputLabel>
                                        <Input
                                            data-testid={`scan-to-actions-default-title-input-${action.id}`}
                                            value={
                                                defaultActions.find(
                                                    (d) => d.id === action.id
                                                )?.title[selectedButtonsLang] ||
                                                ""
                                            }
                                            readOnly
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                cursor: "not-allowed",
                                            }}
                                        />
                                        <InputWrapper>
                                            <Input
                                                data-testid={`scan-to-actions-title-input-${action.id}`}
                                                value={
                                                    action.title[
                                                        selectedButtonsLang
                                                    ] || ""
                                                }
                                                onChange={(e) =>
                                                    onUpdateActionField(
                                                        action.id,
                                                        "title",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={
                                                    defaultActions.find(
                                                        (d) =>
                                                            d.id === action.id
                                                    )?.title[
                                                        selectedButtonsLang
                                                    ] || t("Action title")
                                                }
                                            />
                                            <VisibilityButton
                                                data-testid={`scan-to-actions-title-visibility-${action.id}`}
                                                isVisible={action.titleVisible}
                                                onClick={() =>
                                                    onToggleActionTitleVisible(
                                                        action.id
                                                    )
                                                }
                                                title={
                                                    action.titleVisible
                                                        ? "Masquer le titre"
                                                        : "Afficher le titre"
                                                }
                                            >
                                                {action.titleVisible ? (
                                                    <svg
                                                        viewBox="0 0 24 24"
                                                        fill="currentColor"
                                                    >
                                                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        viewBox="0 0 24 24"
                                                        fill="currentColor"
                                                    >
                                                        <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                                                    </svg>
                                                )}
                                            </VisibilityButton>
                                            <ResetButton
                                                data-testid={`scan-to-actions-reset-title-${action.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetActionTitle(
                                                        action.id
                                                    )
                                                }
                                                title={t("Reset")}
                                            >
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                >
                                                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                                                </svg>
                                            </ResetButton>
                                        </InputWrapper>
                                    </ActionTextRow>

                                    {/* Description row */}
                                    <ActionTextRow>
                                        <InputLabel>
                                            {t("Description")}
                                        </InputLabel>
                                        <Input
                                            data-testid={`scan-to-actions-default-description-input-${action.id}`}
                                            value={
                                                defaultActions.find(
                                                    (d) => d.id === action.id
                                                )?.description[
                                                    selectedButtonsLang
                                                ] || ""
                                            }
                                            readOnly
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                cursor: "not-allowed",
                                            }}
                                        />
                                        <InputWrapper>
                                            <Input
                                                data-testid={`scan-to-actions-description-input-${action.id}`}
                                                value={
                                                    action.description[
                                                        selectedButtonsLang
                                                    ] || ""
                                                }
                                                onChange={(e) =>
                                                    onUpdateActionField(
                                                        action.id,
                                                        "description",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={
                                                    defaultActions.find(
                                                        (d) =>
                                                            d.id === action.id
                                                    )?.description[
                                                        selectedButtonsLang
                                                    ] || t("Action description")
                                                }
                                            />
                                            <VisibilityButton
                                                data-testid={`scan-to-actions-description-visibility-${action.id}`}
                                                isVisible={
                                                    action.descriptionVisible
                                                }
                                                onClick={() =>
                                                    onToggleActionDescVisible(
                                                        action.id
                                                    )
                                                }
                                                title={
                                                    action.descriptionVisible
                                                        ? "Masquer la description"
                                                        : "Afficher la description"
                                                }
                                            >
                                                {action.descriptionVisible ? (
                                                    <svg
                                                        viewBox="0 0 24 24"
                                                        fill="currentColor"
                                                    >
                                                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        viewBox="0 0 24 24"
                                                        fill="currentColor"
                                                    >
                                                        <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                                                    </svg>
                                                )}
                                            </VisibilityButton>
                                            <ResetButton
                                                data-testid={`scan-to-actions-reset-description-${action.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetActionDescription(
                                                        action.id
                                                    )
                                                }
                                                title={t("Reset")}
                                            >
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                >
                                                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                                                </svg>
                                            </ResetButton>
                                        </InputWrapper>
                                    </ActionTextRow>
                                </div>
                            </ActionConfigBody>
                        </ActionConfigCard>
                    ))}
            </div>
        </Card>
    );
}
